import { PopoverPlacement } from "@idui/react-popover";
import { AddCircleOutlined } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  FormControl,
  FormHelperText,
  IconButton,
  TextField,
} from "@mui/material";
import _ from "lodash";
import { useEffect, useMemo, type FC, type SyntheticEvent } from "react";
import { useController, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  dropdownsIsFetchingAtom,
  Log,
  normalizeToArrayValue,
  type ICustomField,
} from "@ryde/shared";

import { AccessControl, FramerTooltip } from "../../common";
import { FullWidthTextFieldWrapper } from "../parts-styled";
import { FieldExplanationIcon } from "./_parts";
import {
  DropdownOptionRenderer,
  getDropdownHelpIconOffsetPx,
  isHighlightedId,
  ListBox,
  renderHighlightedBoundMultiselectValue,
  toHighlightedIdKeySet,
  useDropdownTemplate,
  type THighlightedBoundMultiselectGetItemProps,
} from "./dropdowns";
import { SelectAllPaperWrapper } from "./selectall-paper-wrapper.template";

export const MultiselectBoundTemplate: FC<ICustomField> = ({
  id,
  name,
  formAttributes,
  formLabel,
  formValidation,
  e2eData = "multiselect-bound-template",
  labelTranslated,
}) => {
  const {
    control,
    getValues,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { t } = useTranslation();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const renderValue: number[] = getValues(name);
  const loadingItems = useRecoilValue(dropdownsIsFetchingAtom);
  useEffect(() => {
    if (renderValue && !_.isArray(renderValue)) {
      /* guard for invalid value coming from backend */
      Log.warn(
        t("errors.multiSelectBoundTemplateInitialValueNotArray"),
        renderValue,
      );
      // Autocomplete multiple cannot take a scalar; keep a single id as a one-item selection
      setValue(name, normalizeToArrayValue(renderValue));
    }
  }, [name, renderValue, setValue, t]);

  const {
    isAddButtonVisible,
    requiredComputed,
    isDisabled,
    dropdownOptions,
    handleInsertItem,
    selectAll,
    getOptionLabel,
    setInputText,
    inputText,
    exclusiveIds,
  } = useDropdownTemplate("multi", name, formAttributes, formValidation);
  const {
    field: { value: formValue, ...restField },
    fieldState: { error },
  } = useController({ name, control });

  const {
    dropItemsSource,
    disableSorting,
    highlightedIdsFrom,
    highlightedTooltipTranslationKey,
  } = formAttributes || {};
  const highlightChipsEnabled = Boolean(highlightedIdsFrom);
  const watchedHighlightedIds = useWatch({
    name: highlightedIdsFrom ?? name,
  });
  const highlightedIdKeys = useMemo(
    () =>
      highlightedIdsFrom
        ? toHighlightedIdKeySet(watchedHighlightedIds)
        : new Set<string>(),
    [highlightedIdsFrom, watchedHighlightedIds],
  );
  const highlightedTooltip = highlightedTooltipTranslationKey
    ? t(highlightedTooltipTranslationKey)
    : undefined;
  const sortedValue = useMemo(() => {
    const currentSelection = normalizeToArrayValue<string | number>(formValue);
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const highlightedDelta =
        Number(isHighlightedId({ option: b, highlightedIdKeys })) -
        Number(isHighlightedId({ option: a, highlightedIdKeys }));
      if (highlightedDelta !== 0) {
        return highlightedDelta;
      }
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [formValue, disableSorting, getOptionLabel, highlightedIdKeys]);

  const value = useMemo(() => sortedValue, [sortedValue]);

  const onChangeDropdown = (
    e: SyntheticEvent<Element, Event>,
    newValue: readonly (string | number)[],
  ): void => {
    Log.debug("Dropdown multi template value changed to:", newValue);
    setValue(name, newValue, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <FullWidthTextFieldWrapper>
      <Box sx={{ display: "flex", width: "calc(100% + 48px)" }}>
        <FormControl
          data-e2e-template="multiselect-bound-template"
          data-e2e-field-name={name}
          required={requiredComputed}
          fullWidth
          error={!!error}
        >
          <Autocomplete
            {...restField}
            data-e2e={e2eData}
            data-e2e-id={`${name}_${id || ""}`}
            disabled={isDisabled || loadingItems}
            multiple
            freeSolo
            options={dropdownOptions || []}
            renderOption={(props, option) =>
              DropdownOptionRenderer({
                listItemProps: props,
                option: option as string | number,
                getOptionLabel,
                boldValues: exclusiveIds,
                ...(highlightChipsEnabled && {
                  highlightedIdKeys,
                  highlightedTooltip,
                }),
              })
            }
            renderValue={
              highlightChipsEnabled
                ? (values, getItemProps) =>
                    renderHighlightedBoundMultiselectValue({
                      values,
                      getItemProps:
                        getItemProps as THighlightedBoundMultiselectGetItemProps,
                      getOptionLabel,
                      highlightedIdKeys,
                      highlightedTooltip,
                    })
                : undefined
            }
            value={value}
            slotProps={{
              listbox: {
                component: ListBox,
              },
            }}
            filterOptions={options => options}
            onChange={(event, newValue) => {
              if (!newValue) {
                return;
              }

              checkCleanupNestedValidation(name, errors);

              onChangeDropdown(event, newValue as (string | number)[]);
            }}
            inputValue={inputText}
            onInputChange={(event, newValue) => {
              setInputText(newValue ?? "");
            }}
            isOptionEqualToValue={(opt, val) => String(opt) === String(val)}
            getOptionLabel={getOptionLabel}
            disableCloseOnSelect
            renderInput={params => (
              <TextField
                id={params.id}
                fullWidth={params.fullWidth}
                size={params.size}
                slotProps={params.slotProps}
                error={!!error}
                disabled={isDisabled || loadingItems}
                label={formLabelTranslated}
                required={requiredComputed}
                data-e2e="multiselect-bound-template-input"
              />
            )}
            slots={{
              paper: paperProps => (
                <SelectAllPaperWrapper
                  {...paperProps}
                  selectAll={formValue?.length === dropdownOptions?.length}
                  handleToggleSelectAll={() => {
                    selectAll();
                  }}
                />
              ),
            }}
          />
          {error && (
            <FormHelperText data-e2e="multiselect-bound-template-error">
              {error.message}
            </FormHelperText>
          )}
        </FormControl>
        <Box
          sx={{
            paddingInlineStart: "8px",
            paddingTop: "8px",
            minWidth: "48px",
          }}
        >
          {isAddButtonVisible && (
            <AccessControl
              entity={dropItemsSource || "unknown"}
              action="create"
            >
              <FramerTooltip
                content={t("general.ADD_NEW_DROPDOWN_ITEM_TOOLTIP")}
                placement={PopoverPlacement.top}
              >
                <IconButton onClick={handleInsertItem}>
                  <AddCircleOutlined />
                </IconButton>
              </FramerTooltip>
            </AccessControl>
          )}
        </Box>
      </Box>
      <FieldExplanationIcon
        formAttributes={formAttributes}
        horizontalOffset={getDropdownHelpIconOffsetPx({
          hasAddButton: isAddButtonVisible,
          hasEditButton: false,
        })}
      />
    </FullWidthTextFieldWrapper>
  );
};
